"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useMotionMode } from "@/context/MotionContext";
import { GLITCH_MAP } from "@/hooks/useLetterGlitch";

// =============================================================================
// EXP PROBE
// =============================================================================
// A discoverable ghost element floating outside the orbital ellipse.
// At rest it reads `[ · ]` at near-zero opacity — mistakable for a starfield
// artifact. On hover/focus it decodes character-by-character into `[ /exp ]`
// and links to the experiments index.
//
// NOT a domain. Not part of ring navigation or the arrow-key focus cycle.
// Reachable via Tab after all domain buttons.

interface ExpProbeProps {
  isVisible: boolean;
  onNavigate: () => void;
  crtEnabled: boolean;
  canAnimate: boolean;
}

// Glitch glyph pool — GLITCH_MAP values plus block chars for transmission noise
const GLITCH_POOL = [
  ...new Set(Object.values(GLITCH_MAP)),
  "▓",
  "░",
  "▒",
];

const TARGET = "/exp";
const CHAR_INTERVAL = 180; // ms between each char starting to decode
const GLITCH_HOLD = 150; // ms a char shows glitch glyphs before resolving
const GLITCH_SWAP = 55; // ms between glitch glyph swaps within the hold window
const DECODE_TOTAL = (TARGET.length - 1) * CHAR_INTERVAL + GLITCH_HOLD + 200;
const FREEZE_MS = 350; // pause on mouseleave before snapping back to rest

type ProbeState = "rest" | "decoding" | "resolved" | "fading";

export function ExpProbe({
  isVisible,
  onNavigate,
  crtEnabled,
  canAnimate,
}: ExpProbeProps) {
  const { gpuTier } = useMotionMode();
  const [state, setState] = useState<ProbeState>("rest");
  // Per-character display: glyph + opacity, plus bracket opacity
  const [chars, setChars] = useState<string[]>(() => TARGET.split(""));
  const [charAlpha, setCharAlpha] = useState<number[]>(() =>
    TARGET.split("").map(() => 0)
  );
  const [bracketAlpha, setBracketAlpha] = useState(0.1);

  const rafRef = useRef<number | null>(null);
  const freezeTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Static fallback: no decode animation on T0/T1 or reduced motion
  const staticMode = !canAnimate || gpuTier < 2;

  const cancelAnimation = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (freezeTimerRef.current !== null) {
      clearTimeout(freezeTimerRef.current);
      freezeTimerRef.current = null;
    }
  }, []);

  const toRest = useCallback(() => {
    cancelAnimation();
    setState("rest");
    setChars(TARGET.split(""));
    setCharAlpha(TARGET.split("").map(() => 0));
    setBracketAlpha(0.1);
  }, [cancelAnimation]);

  const reveal = useCallback(() => {
    cancelAnimation();

    // Static / reduced-motion: single crossfade to resolved
    if (staticMode) {
      setState("resolved");
      setChars(TARGET.split(""));
      setCharAlpha(TARGET.split("").map(() => 0.6));
      setBracketAlpha(0.35);
      return;
    }

    setState("decoding");
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const nextChars: string[] = [];
      const nextAlpha: number[] = [];

      for (let i = 0; i < TARGET.length; i++) {
        const charStart = i * CHAR_INTERVAL;
        const real = TARGET[i];
        const isPunct = real === "/" || real === " ";

        if (elapsed < charStart) {
          nextChars.push(real);
          nextAlpha.push(0);
        } else if (elapsed < charStart + GLITCH_HOLD && !isPunct) {
          // Glitch phase: swap glyphs, low opacity
          const swapIndex = Math.floor((elapsed - charStart) / GLITCH_SWAP);
          // Deterministic-ish per swap window so it doesn't strobe every frame
          nextChars.push(GLITCH_POOL[(i + swapIndex) % GLITCH_POOL.length]);
          nextAlpha.push(0.2);
        } else {
          // Resolved
          nextChars.push(real);
          nextAlpha.push(0.6);
        }
      }

      const bracket =
        0.1 + 0.25 * Math.min(1, elapsed / DECODE_TOTAL);

      setChars(nextChars);
      setCharAlpha(nextAlpha);
      setBracketAlpha(bracket);

      if (elapsed < DECODE_TOTAL) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setState("resolved");
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [cancelAnimation, staticMode]);

  const dismiss = useCallback(() => {
    cancelAnimation();
    if (staticMode) {
      toRest();
      return;
    }
    // Freeze current frame briefly, then cut back to rest (no reverse decode)
    setState("fading");
    freezeTimerRef.current = setTimeout(() => {
      toRest();
    }, FREEZE_MS);
  }, [cancelAnimation, staticMode, toRest]);

  // Cleanup on unmount
  useEffect(() => cancelAnimation, [cancelAnimation]);

  // Reset to rest if the overview hides while mid-decode
  useEffect(() => {
    if (!isVisible) toRest();
  }, [isVisible, toRest]);

  return (
    <button
      type="button"
      data-exp-probe
      onClick={onNavigate}
      onMouseEnter={reveal}
      onMouseLeave={dismiss}
      onFocus={reveal}
      onBlur={dismiss}
      aria-label="Navigate to experiments"
      className="absolute bg-transparent border-none outline-none cursor-pointer select-none font-mono"
      style={{
        top: "14vh",
        left: "84vw",
        fontSize: 15,
        letterSpacing: "0.18em",
        color: "#FFD000",
        textAlign: "center",
        transform: `translate(-50%, 0) scale(${isVisible ? 1 : 0.5})`,
        opacity: isVisible ? 1 : 0,
        transition: canAnimate
          ? "opacity 300ms ease-out 460ms, transform 400ms cubic-bezier(0.34, 1.56, 0.64, 1) 460ms"
          : "none",
        textShadow: crtEnabled ? "var(--crt-text-shadow)" : "none",
        whiteSpace: "pre",
      }}
    >
      <span style={{ opacity: bracketAlpha }}>[ </span>
      {state === "rest" ? (
        <span style={{ opacity: 0.08 }}>·</span>
      ) : (
        chars.map((c, i) => (
          <span key={i} style={{ opacity: charAlpha[i] }}>
            {c}
          </span>
        ))
      )}
      <span style={{ opacity: bracketAlpha }}> ]</span>
    </button>
  );
}
